import { common } from '@mui/material/colors';
import { ThemeOptions, alpha } from '@mui/material/styles';

// Legacy pages can add this attribute when a component owns semantic colors itself.
const DARK_OVERRIDE_ESCAPE_HATCH = ':not([data-no-dark-override], [data-no-dark-override] *)';

export const DARK_THEME_COLOURS = {
  backgroundDefault: '#101418',
  backgroundPaper: '#181d23',
  backgroundElevated: '#202832',
  textPrimary: '#f3f6f9',
  textSecondary: '#c5cbd3',
  textDisabled: '#8d96a0',
} as const;

export const DARK_THEME_GREY = {
  50: DARK_THEME_COLOURS.backgroundPaper,
  100: DARK_THEME_COLOURS.backgroundElevated,
  200: alpha(common.white, 0.12),
  300: alpha(common.white, 0.24),
  400: alpha(common.white, 0.38),
  500: alpha(common.white, 0.54),
  600: alpha(common.white, 0.68),
  700: alpha(common.white, 0.78),
  800: alpha(common.white, 0.88),
  900: DARK_THEME_COLOURS.textPrimary,
  A100: DARK_THEME_COLOURS.backgroundElevated,
  A200: alpha(common.white, 0.24),
  A400: alpha(common.white, 0.54),
  A700: alpha(common.white, 0.78),
} as const;

export function createDarkModeComponentOverrides(accentColor: string): ThemeOptions['components'] {
  const selectedBackground = alpha(accentColor, 0.16);
  const selectedHoverBackground = alpha(accentColor, 0.22);
  const hoverBackground = alpha(common.white, 0.08);
  const subtleBorder = alpha(common.white, 0.12);
  const scrollbarThumb = alpha(common.white, 0.32);
  const scrollbarThumbHover = alpha(common.white, 0.48);
  const scrollbarTrack = DARK_THEME_COLOURS.backgroundDefault;

  return {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          colorScheme: 'dark',
          scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
        },
        '*': {
          scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
        },
        '*::-webkit-scrollbar-track': {
          backgroundColor: scrollbarTrack,
        },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: scrollbarThumb,
          border: `3px solid ${scrollbarTrack}`,
          borderRadius: 8,
        },
        '*::-webkit-scrollbar-thumb:hover': {
          backgroundColor: scrollbarThumbHover,
        },
        '*::-webkit-scrollbar-corner': {
          backgroundColor: scrollbarTrack,
        },
        body: {
          color: DARK_THEME_COLOURS.textPrimary,
          backgroundColor: DARK_THEME_COLOURS.backgroundDefault,
        },
        '#app-container': {
          minHeight: '100vh',
          color: DARK_THEME_COLOURS.textPrimary,
          backgroundColor: DARK_THEME_COLOURS.backgroundDefault,
        },
        '#app-container main': {
          color: DARK_THEME_COLOURS.textPrimary,
          backgroundColor: DARK_THEME_COLOURS.backgroundDefault,
        },
        [[
          `#app-container main h1${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main h2${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main h3${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main h4${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main h5${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main h6${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main p:not(.MuiFormHelperText-root)${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main label${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main legend${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main th${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main td${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main dt${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main dd${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main li${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main span:not([class]):not([style])${DARK_OVERRIDE_ESCAPE_HATCH}`,
        ].join(',')]: {
          color: DARK_THEME_COLOURS.textPrimary,
        },
        [[
          `#app-container main a:not(.MuiButton-root)${DARK_OVERRIDE_ESCAPE_HATCH}`,
          `#app-container main a:not(.MuiButton-root)${DARK_OVERRIDE_ESCAPE_HATCH} span${DARK_OVERRIDE_ESCAPE_HATCH}`,
        ].join(',')]: {
          color: accentColor,
        },
        '#app-container main .text-muted, #app-container main small': {
          color: DARK_THEME_COLOURS.textSecondary,
        },
        '#app-container main .button-container': {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
          borderTop: `1px solid ${subtleBorder}`,
        },
        [`#app-container main pre${DARK_OVERRIDE_ESCAPE_HATCH}`]: {
          backgroundColor: DARK_THEME_COLOURS.backgroundElevated,
          color: DARK_THEME_COLOURS.textPrimary,
          padding: '12px 16px',
          borderRadius: 4,
          border: `1px solid ${subtleBorder}`,
          overflow: 'auto',
        },
        [`#app-container main code${DARK_OVERRIDE_ESCAPE_HATCH}`]: {
          backgroundColor: DARK_THEME_COLOURS.backgroundElevated,
          padding: '2px 6px',
          borderRadius: 4,
          fontSize: '0.875em',
        },
        [`#app-container main pre${DARK_OVERRIDE_ESCAPE_HATCH} code${DARK_OVERRIDE_ESCAPE_HATCH}`]: {
          padding: 0,
          border: 'none',
          backgroundColor: 'transparent',
          fontSize: 'inherit',
        },
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: 'inherit',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: ({ theme }) => ({
          color: DARK_THEME_COLOURS.textSecondary,
          '&.Mui-disabled': {
            color: DARK_THEME_COLOURS.textDisabled,
          },
          '&.Mui-error': {
            color: theme.palette.error.main,
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: DARK_THEME_COLOURS.textSecondary,
          '&.Mui-focused': {
            color: accentColor,
          },
        },
      },
    },
    MuiInputBase: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          color: DARK_THEME_COLOURS.textPrimary,
          backgroundColor: DARK_THEME_COLOURS.backgroundDefault,
        },
        input: {
          color: DARK_THEME_COLOURS.textPrimary,
          '&::placeholder': {
            color: DARK_THEME_COLOURS.textDisabled,
            opacity: 1,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: alpha(common.white, 0.24),
        },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(common.white, 0.48),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: accentColor,
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        icon: {
          color: DARK_THEME_COLOURS.textSecondary,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          borderColor: alpha(common.white, 0.16),
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: DARK_THEME_COLOURS.textSecondary,
          '&.Mui-selected': {
            color: accentColor,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
          color: DARK_THEME_COLOURS.textPrimary,
          border: `1px solid ${subtleBorder}`,
        },
        list: {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
          color: DARK_THEME_COLOURS.textPrimary,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: DARK_THEME_COLOURS.textPrimary,
          '&.Mui-selected': {
            backgroundColor: selectedBackground,
          },
          '&.Mui-selected:hover': {
            backgroundColor: selectedHoverBackground,
          },
          '&:hover': {
            backgroundColor: hoverBackground,
          },
          '& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiSvgIcon-root': {
            color: 'inherit',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: DARK_THEME_COLOURS.textSecondary,
          '&.Mui-selected': {
            backgroundColor: selectedBackground,
            color: DARK_THEME_COLOURS.textPrimary,
          },
          '&.Mui-selected:hover': {
            backgroundColor: selectedHoverBackground,
          },
          '&:hover': {
            backgroundColor: hoverBackground,
            color: DARK_THEME_COLOURS.textPrimary,
          },
          '& .MuiListItemIcon-root, & .MuiListItemText-primary, & .MuiListItemText-secondary, & .MuiSvgIcon-root': {
            color: 'inherit',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: DARK_THEME_COLOURS.textSecondary,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: 'inherit',
        },
        secondary: {
          color: DARK_THEME_COLOURS.textSecondary,
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
          color: DARK_THEME_COLOURS.textPrimary,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          borderColor: subtleBorder,
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
          '&.Mui-expanded': {
            backgroundColor: alpha(accentColor, 0.12),
          },
          '&:hover': {
            backgroundColor: alpha(accentColor, 0.08),
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
          color: DARK_THEME_COLOURS.textPrimary,
        },
        columnHeader: {
          backgroundColor: DARK_THEME_COLOURS.backgroundElevated,
        },
        columnHeaders: {
          background: DARK_THEME_COLOURS.backgroundElevated,
        },
        cell: {
          borderColor: subtleBorder,
        },
        row: {
          '&:hover': {
            backgroundColor: alpha(accentColor, 0.08),
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: DARK_THEME_COLOURS.backgroundDefault,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        listbox: {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
          color: DARK_THEME_COLOURS.textPrimary,
        },
        option: {
          color: DARK_THEME_COLOURS.textPrimary,
          '&[aria-selected="true"]': {
            backgroundColor: selectedBackground,
          },
          '&.Mui-focused': {
            backgroundColor: hoverBackground,
          },
        },
        paper: {
          backgroundColor: DARK_THEME_COLOURS.backgroundPaper,
          color: DARK_THEME_COLOURS.textPrimary,
          border: `1px solid ${subtleBorder}`,
        },
        noOptions: {
          color: DARK_THEME_COLOURS.textSecondary,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: DARK_THEME_COLOURS.backgroundElevated,
          color: DARK_THEME_COLOURS.textPrimary,
          border: `1px solid ${subtleBorder}`,
          fontSize: '0.75rem',
        },
        arrow: {
          color: DARK_THEME_COLOURS.backgroundElevated,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          color: DARK_THEME_COLOURS.textPrimary,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: subtleBorder,
        },
        head: {
          backgroundColor: DARK_THEME_COLOURS.backgroundElevated,
          color: DARK_THEME_COLOURS.textPrimary,
        },
      },
    },
  };
}
